import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useConversationHistory from "@/hooks/assistant/useConversationHistory";
import { cn } from "@/lib/utils";
import type { ConversationSummary } from "@cs_teamwaydev/mailvista-sdk_react";
import {
    ChevronDown,
    Ellipsis,
    PanelLeft,
    Search,
    SquarePen,
} from "lucide-react";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { assistantIconButtonClassName } from "./assistantClasses";
import AssistantLogo from "./AssistantLogo";

// Search by keyword from two characters, once typing pauses.
const SEARCH_MIN_LENGTH = 2;
const SEARCH_DELAY_MS = 300;

/** The day of a conversation (its last question), as a key: « 2026-10-07 », in local time. */
const historyDay = (updatedAt: string) => {
    const date = new Date(updatedAt);

    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
};

const actionClassName =
    "rounded-md px-1.5 py-0.5 text-xs font-medium transition-colors";
const currentRowClassName =
    "bg-assistant-lavender font-semibold hover:bg-assistant-lavender";
const headingClassName =
    "px-2 pb-2 text-xs font-semibold uppercase leading-4 tracking-[0.02em] text-assistant-muted";
// A long title fades out over the last few letters of its row instead of ending in « … »; a title
// that fits is left alone.
const fadingTitleClassName =
    "min-w-0 flex-1 overflow-hidden whitespace-nowrap [mask-image:linear-gradient(to_left,transparent,black_1.75rem)]";
const menuItemClassName = "rounded-md px-2 py-1.5 text-[13px]";
const menuButtonClassName =
    "h-7 w-7 flex-none items-center justify-center rounded-md text-assistant-muted hover:bg-white hover:text-primary-darkest";

/** « Supprimer … ? » asked in place, with « Oui » and « Non ». */
const InlineConfirm = ({
    question,
    truncate = false,
    noClassName,
    onConfirm,
    onCancel,
}: {
    question: string;
    truncate?: boolean;
    noClassName: string;
    onConfirm: () => void;
    onCancel: () => void;
}) => {
    const { t } = useTranslation();

    return (
        <>
            <span className={cn("min-w-0 flex-1", truncate && "truncate")}>
                {question}
            </span>
            <button
                className={cn(actionClassName, "bg-error-darkest text-white")}
                onClick={onConfirm}
                type="button"
            >
                {t("vistassistant.history.yes")}
            </button>
            <button
                className={cn(actionClassName, noClassName)}
                onClick={onCancel}
                type="button"
            >
                {t("vistassistant.history.no")}
            </button>
        </>
    );
};

/** A conversation's state, as the dot before its title says it. */
type ConversationStatus = "read" | "running" | "unread";

/**
 * The dot before a conversation, as in Claude's sidebar, always filled, one colour per state: grey when
 * everything is read, violet pulsing while the assistant answers, the brand's pink for an answer not
 * seen yet.
 */
const StatusDot = ({ status }: { status: ConversationStatus }) => {
    const { t } = useTranslation();
    const label = t(`vistassistant.history.status.${status}`);
    return (
        <>
            <span
                aria-hidden
                className={cn(
                    "size-2 flex-none rounded-full",
                    status === "read" && "bg-neutral-light",
                    status === "running" && "animate-pulse bg-primary-dark",
                    status === "unread" && "bg-primary-pink",
                )}
                title={label}
            />
            {/* Read aloud only when there is something to say: « …, Réponse à lire ». */}
            {status !== "read" && (
                <span className="sr-only">{`${label}, `}</span>
            )}
        </>
    );
};

interface ConversationHistoryProps {
    /** The conversation shown in the panel: highlighted. */
    currentId?: string;
    /** The conversation whose answer is on its way (only the one shown: one turn at a time). */
    runningId?: string;
    /** The conversation with an answer that arrived while the assistant was closed, not seen yet. */
    unreadId?: string;
    /** Opens a conversation; true once it is shown. */
    onOpen: (id: string) => Promise<boolean>;
    /** « Nouvelle conversation », from the menu. */
    onNewConversation: () => void;
    /** The conversation shown was deleted: the panel starts a new one. */
    onCurrentDeleted: () => void;
    onClose: () => void;
    isCovering: boolean;
    onHide: () => void;
    onCloseAssistant: () => void;
}

/**
 * The assistant's past conversations (step-based contract), on the left of the panel, like Claude's:
 * a keyword search, « Nouvelle conversation », then the conversations by day
 * (« Aujourd'hui », « 7 oct. »…). « ⋯ » renames or deletes one; « Tout supprimer » at the bottom.
 * Deleting asks once, in the line itself. A search lists the conversations where the word appears,
 * with an excerpt.
 */
const ConversationHistory = ({
    currentId,
    runningId,
    unreadId,
    onOpen,
    onNewConversation,
    onCurrentDeleted,
    onClose,
    isCovering,
    onHide,
    onCloseAssistant,
}: ConversationHistoryProps) => {
    const { t, i18n } = useTranslation();
    const [search, setSearch] = useState("");
    const [keyword, setKeyword] = useState("");
    const [renaming, setRenaming] = useState<
        { id: string; title: string } | undefined
    >(undefined);
    // A conversation's id, or « all » (the whole history).
    const [confirming, setConfirming] = useState<string | "all" | undefined>(
        undefined,
    );
    const [opening, setOpening] = useState<string | undefined>(undefined);

    useEffect(() => {
        const typed = search.trim();
        const timer = setTimeout(
            () => setKeyword(typed.length >= SEARCH_MIN_LENGTH ? typed : ""),
            SEARCH_DELAY_MS,
        );

        return () => clearTimeout(timer);
    }, [search]);

    const { conversations, results, rename, remove, removeAll } =
        useConversationHistory({ keyword, currentId, onCurrentDeleted });

    const saveTitle = () => {
        const title = renaming?.title.trim();
        if (renaming && title) rename.mutate({ id: renaming.id, title });
        setRenaming(undefined);
    };

    const open = async (id: string) => {
        if (opening) return;
        setOpening(id);
        try {
            if (await onOpen(id)) onClose();
        } finally {
            setOpening(undefined);
        }
    };

    const today = historyDay(new Date().toISOString());
    const dayLabel = (updatedAt: string) => {
        if (historyDay(updatedAt) === today)
            return t("vistassistant.history.today");
        const date = new Date(updatedAt);

        return date.toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "long",
            ...(date.getFullYear() !== new Date().getFullYear()
                ? { year: "numeric" }
                : {}),
        });
    };

    const all = conversations.data ?? [];
    // By day, the most recent first (the list already comes in that order).
    const days = all.reduce<{ day: string; items: ConversationSummary[] }[]>(
        (groups, conversation) => {
            const day = historyDay(conversation.updatedAt);
            const last = groups[groups.length - 1];
            if (last?.day === day) last.items.push(conversation);
            else groups.push({ day, items: [conversation] });

            return groups;
        },
        [],
    );

    const titleOf = (conversation: ConversationSummary) =>
        conversation.title || t("vistassistant.history.untitled");

    const row = (conversation: ConversationSummary) => {
        const title = titleOf(conversation);
        const isCurrent = conversation.id === currentId;

        if (renaming?.id === conversation.id) {
            return (
                <li key={conversation.id} className="px-0.5 py-0.5">
                    <input
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                        aria-label={t("vistassistant.history.rename")}
                        className="h-10 w-full rounded-lg border border-primary-dark px-2 text-sm text-primary-darkest outline-none"
                        maxLength={120}
                        onBlur={saveTitle}
                        onChange={(e) =>
                            setRenaming({
                                id: conversation.id,
                                title: e.target.value,
                            })
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter") saveTitle();
                            if (e.key === "Escape") setRenaming(undefined);
                        }}
                        value={renaming.title}
                    />
                </li>
            );
        }

        if (confirming === conversation.id) {
            return (
                <li
                    key={conversation.id}
                    className="flex h-10 items-center gap-1 rounded-lg bg-error-lightest px-2 text-[13px] text-error-darkest"
                >
                    <InlineConfirm
                        noClassName="hover:bg-white"
                        onCancel={() => setConfirming(undefined)}
                        onConfirm={() => {
                            setConfirming(undefined);
                            remove.mutate(conversation.id);
                        }}
                        question={t("vistassistant.history.confirmDelete")}
                        truncate
                    />
                </li>
            );
        }

        return (
            <li
                key={conversation.id}
                className={cn(
                    "group flex items-center rounded-lg transition-colors hover:bg-assistant-chip",
                    isCurrent && currentRowClassName,
                )}
            >
                <button
                    aria-current={isCurrent || undefined}
                    className={cn(
                        "flex h-10 min-w-0 flex-1 items-center px-2 text-left text-sm text-primary-darkest",
                        opening === conversation.id && "opacity-60",
                    )}
                    onClick={() => open(conversation.id)}
                    title={title}
                    type="button"
                >
                    <StatusDot
                        status={
                            conversation.id === runningId
                                ? "running"
                                : conversation.id === unreadId
                                  ? "unread"
                                  : "read"
                        }
                    />
                    <span className={cn(fadingTitleClassName, "ml-2.5")}>
                        {title}
                    </span>
                </button>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            aria-label={t("vistassistant.history.options", {
                                title,
                            })}
                            className={cn(
                                menuButtonClassName,
                                // Shown on hover, and as soon as the row has the keyboard focus
                                // (a hidden button can't take it: Tab from the title reaches it).
                                "mr-0.5 hidden group-focus-within:flex group-hover:flex data-[state=open]:flex [@media(hover:none)]:flex",
                            )}
                            type="button"
                        >
                            <Ellipsis aria-hidden className="size-4" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="w-48 rounded-lg p-1"
                    >
                        <DropdownMenuItem
                            className={menuItemClassName}
                            onSelect={() =>
                                setRenaming({
                                    id: conversation.id,
                                    title: conversation.title ?? "",
                                })
                            }
                        >
                            {t("vistassistant.history.rename")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            className={cn(
                                menuItemClassName,
                                "text-error-darkest",
                            )}
                            onSelect={() => setConfirming(conversation.id)}
                        >
                            {t("vistassistant.history.delete")}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </li>
        );
    };

    const message = (text: string) => (
        <p className="px-2 text-sm leading-5 text-assistant-muted">{text}</p>
    );

    const hasNoConversations =
        conversations.isLoading ||
        conversations.isError ||
        (conversations.isSuccess && all.length === 0);

    return (
        <aside
            aria-label={t("vistassistant.history.title")}
            className="flex w-[280px] flex-none flex-col bg-white max-md:absolute max-md:inset-0 max-md:z-20 max-md:w-full"
        >
            <div className="flex flex-col gap-4 px-4 pt-5">
                <div className="flex items-center gap-2.5">
                    <AssistantLogo size="md" />
                    <h2 className="min-w-0 flex-1 truncate font-display text-xl font-bold text-primary-darkest">
                        {t("vistassistant.title")}
                    </h2>
                    {isCovering && (
                        <>
                            <Button
                                aria-label={t("vistassistant.history.title")}
                                aria-pressed
                                className={cn(
                                    assistantIconButtonClassName,
                                    "hover:bg-assistant-chip",
                                )}
                                onClick={onHide}
                                title={t("vistassistant.history.title")}
                                variant="ghost"
                            >
                                <PanelLeft strokeWidth={1.5} />
                            </Button>
                            <Button
                                aria-label={t("vistassistant.closeAssistant")}
                                className={cn(
                                    assistantIconButtonClassName,
                                    "hover:bg-assistant-chip",
                                )}
                                onClick={onCloseAssistant}
                                title={t("vistassistant.closeAssistant")}
                                variant="ghost"
                            >
                                <ChevronDown strokeWidth={1.5} />
                            </Button>
                        </>
                    )}
                </div>
                <button
                    className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-assistant-lavender px-4 text-sm font-semibold text-assistant-ink transition-colors hover:bg-primary-lighter"
                    onClick={() => {
                        onNewConversation();
                        onClose();
                    }}
                    type="button"
                >
                    <SquarePen
                        aria-hidden
                        className="size-5 flex-none"
                        strokeWidth={1.5}
                    />
                    {t("vistassistant.newConversation")}
                </button>
                <label className="flex h-11 items-center gap-2.5 rounded-xl border border-assistant-outline bg-white px-4 transition-colors focus-within:border-primary-dark">
                    <Search
                        aria-hidden
                        className="size-5 flex-none text-primary-darkest"
                        strokeWidth={1.5}
                    />
                    <input
                        aria-label={t("vistassistant.history.search")}
                        className="min-w-0 flex-1 text-ellipsis bg-transparent text-base text-primary-darkest outline-none placeholder:text-assistant-muted"
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("vistassistant.history.search")}
                        type="search"
                        value={search}
                    />
                </label>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                {keyword ? (
                    <section className="pt-6">
                        <h3 className={headingClassName}>
                            {t("vistassistant.history.results")}
                        </h3>
                        {results.isLoading &&
                            message(t("vistassistant.history.searching"))}
                        {results.isError &&
                            message(t("vistassistant.history.unavailable"))}
                        {results.isSuccess &&
                            results.data.length === 0 &&
                            message(t("vistassistant.history.noMatch"))}
                        <ul>
                            {(results.data ?? []).map((conversation) => (
                                <li key={conversation.id}>
                                    <button
                                        className={cn(
                                            "flex w-full flex-col gap-0.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-assistant-chip",
                                            opening === conversation.id &&
                                                "opacity-60",
                                        )}
                                        onClick={() => open(conversation.id)}
                                        type="button"
                                    >
                                        <span className="truncate text-sm text-primary-darkest">
                                            {titleOf(conversation)}
                                        </span>
                                        {conversation.excerpt && (
                                            <span className="line-clamp-2 text-xs text-assistant-muted">
                                                {conversation.excerpt}
                                            </span>
                                        )}
                                        <span className="text-[11px] text-assistant-muted/80">
                                            {dayLabel(conversation.updatedAt)}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : (
                    <>
                        {hasNoConversations && (
                            <section className="pt-6">
                                <h3 className={headingClassName}>
                                    {t("vistassistant.history.title")}
                                </h3>
                                {conversations.isLoading &&
                                    message(t("vistassistant.history.loading"))}
                                {conversations.isError &&
                                    message(
                                        t("vistassistant.history.unavailable"),
                                    )}
                                {conversations.isSuccess &&
                                    message(t("vistassistant.history.empty"))}
                            </section>
                        )}
                        {days.map(({ day, items }) => (
                            <section key={day} className="pt-6">
                                <h3 className={headingClassName}>
                                    {dayLabel(items[0].updatedAt)}
                                </h3>
                                <ul className="flex flex-col gap-1">
                                    {items.map(row)}
                                </ul>
                            </section>
                        ))}
                    </>
                )}
            </div>

            {all.length > 0 && !keyword && (
                <div className="border-t border-assistant-divider px-4 py-2">
                    {confirming === "all" ? (
                        <div className="flex items-center gap-1 text-[13px] text-error-darkest">
                            <InlineConfirm
                                noClassName="hover:bg-primary-lightest"
                                onCancel={() => setConfirming(undefined)}
                                onConfirm={() => {
                                    setConfirming(undefined);
                                    removeAll.mutate();
                                }}
                                question={t(
                                    "vistassistant.history.confirmDeleteAll",
                                )}
                            />
                        </div>
                    ) : (
                        <button
                            className="h-7 rounded-md px-1.5 text-xs font-medium text-neutral-dark hover:bg-primary-lightest hover:text-error-darkest"
                            onClick={() => setConfirming("all")}
                            type="button"
                        >
                            {t("vistassistant.history.deleteAll")}
                        </button>
                    )}
                </div>
            )}
        </aside>
    );
};

// Protected: typing in the input re-renders the assistant, not the history (audit 15/10, P4-02).
export default memo(ConversationHistory);
